import { useEffect, useMemo } from "react"

import useSWR from "swr"

import {
  getMultiUnitGridABTestingVariant,
  MultiUnitGridABTestVariants,
} from "@/app/src/hooks/useMultiUnitGridABTesting"
import { getSearchPviAlgoABTestVariant } from "@/app/src/hooks/usePVIABTesting"
import { MAP_AREA } from "@/constant"
import { apiClientCommon } from "services/apiClientCommon"
import { useAppDispatch, useAppSelector } from "stores/hooks"
import {
  selectSearchQuery,
  updateApiRequestType,
} from "stores/search/searchSlice"
import { SearchApiRequestType, SearchType } from "stores/search/types"
import { getCookie } from "utils/cookies"

import useRecentlySearches from "./useRecentlySearches"

const searchPropertiesFetcher = async ({
  url,
  ...params
}: {
  url: string
} & Record<string, string>) => {
  const newSearchData = await apiClientCommon.getPropertiesGet({ params })

  // to show nearby properties when there is no properties on searched location.
  const { content, nearbyProperties, propertyCount } = newSearchData.data.data
  // content is empty and nearby propertyCount is greater then 0 add those properties in content.
  if (
    content &&
    content.length <= 0 &&
    propertyCount > 0 &&
    (params.nearByProperties || !params.isMapDragged)
  ) {
    newSearchData.data.data.content = [...nearbyProperties]
  }
  return newSearchData.data.data
}

const useSearchListingProperties = () => {
  const { saveRecentlySearches } = useRecentlySearches()
  const searchQuery = useAppSelector(selectSearchQuery)
  const dispatch = useAppDispatch()
  const isGoogle = getCookie("isGoogle") === "true"
  const searchPviAlgoVariant = getSearchPviAlgoABTestVariant()
  const multiUnitCollapsedEnabled =
    getMultiUnitGridABTestingVariant() ===
    MultiUnitGridABTestVariants.MULTI_UNIT

  const response = useSWR(
    searchQuery
      ? {
          url: `/api/search`,
          multiUnitCollapsedEnabled,
          ...searchQuery,
          pviVersion: searchPviAlgoVariant,
          allInPriceToggle: true,
          ...(searchQuery?.name?.toLowerCase() === "near by"
            ? { isMapDragged: true }
            : {}),
          isgoogle: isGoogle || false,
        }
      : null, // to resolve multiple api call issue
    searchPropertiesFetcher,
    {
      loadingTimeout: 15000,
      revalidateOnFocus: false,
      dedupingInterval: 3000,
      errorRetryCount: 3,
      keepPreviousData: true,
    },
  )

  // Create a memoized object with only the watched parameters
  const watchedSearchParams = useMemo(
    () => ({
      checkInDate: searchQuery?.startDate || "",
      checkOutDate: searchQuery?.endDate || "",
      placeId: searchQuery?.placeId || "",
      adultCount: searchQuery?.adultCount || 0,
      childCount: searchQuery?.childrenCount || 0,
      name: searchQuery?.name || "",
      petAllowed: searchQuery?.petAllowed || false,
      lat: searchQuery?.lat || 0,
      lng: searchQuery?.lng || 0,
      createdDate: new Date().toISOString(),
    }),
    [
      searchQuery?.startDate,
      searchQuery?.endDate,
      searchQuery?.placeId,
      searchQuery?.adultCount,
      searchQuery?.childrenCount,
      searchQuery?.name,
      searchQuery?.petAllowed,
      searchQuery?.lat,
      searchQuery?.lng,
    ],
  )

  // Effect to save recently searched locations
  useEffect(() => {
    if (
      !watchedSearchParams.name ||
      watchedSearchParams?.name.toLocaleLowerCase() === MAP_AREA
    )
      return
    saveRecentlySearches({
      searchedLocation: [{ ...watchedSearchParams }],
    })
  }, [saveRecentlySearches, watchedSearchParams])

  /**
    We need to call search API multiple times based on different scenarios like
    - search with polygon
    - search without polygon
    - search near by properties
    Logic for this is in "SearchResults" component.

    below logic will set the last api request type in store
    based on that we are able to call search api multiple times in sequence
    sequence is as below
    1. with polygon => Default
    2. without polygon
    3. near by properties

    Search for "useEffect for automatically removing the boundary" and "useEffect to search for nearby properties"
    for logic details.
  */
  // Start: Logic to set last api request type
  let apiRequestType: SearchApiRequestType = ""

  if (searchQuery?.withPolygon) {
    apiRequestType = SearchType.withPolygon
  } else {
    apiRequestType = SearchType.withoutPolygon
  }
  if (searchQuery?.nearByProperties) {
    apiRequestType = SearchType.nearByProperties
  }
  dispatch(updateApiRequestType({ apiRequestType }))
  // End: Logic to set last api request type

  return response
}

export default useSearchListingProperties
